import React from 'react';
import { FiAlertCircle, FiAlertTriangle } from 'react-icons/fi';
import { Badge } from '../../../components/common';
import { formatINR } from '../billingUtils';
import './ImportPreviewTable.css';

export const ACTION_BADGES = {
  create: { variant: 'success', label: 'New' },
  match: { variant: 'primary', label: 'Matched' },
  possible_match: { variant: 'warning', label: 'Possible match' },
  error: { variant: 'danger', label: 'Error' },
  ignored: { variant: 'default', label: 'Ignored' },
};

const hasErrors = (block) =>
  block.action === 'error' || block.errors.length > 0 || block.projects.some((p) => p.error);

/** Client-side filters over the preview blocks. */
export const PREVIEW_FILTERS = [
  { value: 'all', label: 'All', test: () => true },
  { value: 'errors', label: 'Errors', test: hasErrors },
  { value: 'warnings', label: 'Warnings', test: (b) => b.warnings.length > 0 || b.gstin_invalid },
  { value: 'new', label: 'New', test: (b) => b.action === 'create' },
  { value: 'matched', label: 'Matched', test: (b) => b.action === 'match' },
  { value: 'possible', label: 'Possible match', test: (b) => b.action === 'possible_match' },
];

const rowRange = (rows = []) => {
  if (!rows.length) return '';
  const min = Math.min(...rows);
  const max = Math.max(...rows);
  return min === max ? `Row ${min}` : `Rows ${min}–${max}`;
};

const matchLabel = (type) => (type === 'gstin' ? 'by GSTIN' : type === 'client_code' ? 'by Client ID' : '');

/**
 * One card per client block of the import preview, with nested project rows.
 * `decisions` is `{ [block.key]: { action: 'merge'|'create', clientId } }`.
 */
const ImportPreviewTable = ({ blocks, decisions, onDecide, importInvalidGstin = false }) => {
  if (!blocks.length) return <div className="bl-empty">Nothing to show for this filter.</div>;

  return (
    <div className="ipt">
      {blocks.map((block) => {
        const badge = ACTION_BADGES[block.action] ?? { variant: 'default', label: block.action };
        const decision = decisions[block.key];
        const skippedForGstin = block.gstin_invalid && !importInvalidGstin;
        const skipped = block.action === 'error' || block.action === 'ignored' || skippedForGstin;
        const matched = block.action === 'match' ? block.candidates?.[0] : null;
        const radioName = `ipt-decision-${block.key}`;
        // `errors` already repeats each project's error, which is shown inline on its row.
        const projectErrors = new Set(block.projects.map((p) => p.error).filter(Boolean));
        const blockErrors = block.errors.filter((msg) => !projectErrors.has(msg));

        return (
          <section
            key={block.key}
            className={`ipt-block is-${block.action} ${skipped ? 'is-skipped' : ''} ${block.action === 'possible_match' && !decision ? 'needs-decision' : ''}`}
          >
            <header className="ipt-head">
              <div className="ipt-head-main">
                <div className="ipt-title-row">
                  <Badge variant={badge.variant} size="small">{badge.label}</Badge>
                  <h4 className="ipt-name">{block.name || <span className="bl-muted">No client name</span>}</h4>
                  {block.gstin_invalid && <Badge variant="warning" size="small">Invalid GSTIN</Badge>}
                  {skippedForGstin && <Badge variant="default" size="small">Will be skipped</Badge>}
                </div>
                <div className="ipt-meta">
                  <span>GSTIN: <span className="bl-mono">{block.gstin || '—'}</span></span>
                  <span>
                    Client ID:{' '}
                    {block.client_code_auto || !block.client_code ? (
                      <em>ID will be generated</em>
                    ) : (
                      <span className="bl-mono">{block.client_code}</span>
                    )}
                  </span>
                  {block.address && <span className="ipt-address">{block.address}</span>}
                </div>
                {matched && (
                  <div className="ipt-matched">
                    Matches existing client <strong>{matched.name}</strong> {matchLabel(block.match_type)}
                    {matched.client_code && <> · <span className="bl-mono">{matched.client_code}</span></>}
                  </div>
                )}
              </div>
              <span className="ipt-rows">{rowRange(block.row_numbers)}</span>
            </header>

            {block.action === 'possible_match' && (
              <fieldset className="ipt-decision">
                <legend>An existing client has the same name. Choose what to do:</legend>
                {(block.candidates ?? []).map((candidate) => (
                  <label key={candidate.id} className="ipt-option">
                    <input
                      type="radio"
                      name={radioName}
                      checked={decision?.action === 'merge' && decision.clientId === candidate.id}
                      onChange={() => onDecide(block.key, { action: 'merge', clientId: candidate.id })}
                    />
                    <span>
                      Merge into <strong>{candidate.name}</strong>
                      {candidate.deleted && <> <Badge variant="default" size="small">Deleted</Badge></>}
                      <span className="bl-cell-sub">
                        {' '}
                        {[candidate.gstin || 'No GSTIN', candidate.client_code].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </label>
                ))}
                <label className="ipt-option">
                  <input
                    type="radio"
                    name={radioName}
                    checked={decision?.action === 'create'}
                    onChange={() => onDecide(block.key, { action: 'create' })}
                  />
                  <span>Create a new client</span>
                </label>
              </fieldset>
            )}

            {block.projects.length > 0 && (
              <div className="bl-table-wrap">
                <table className="bl-table ipt-projects">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Description</th>
                      <th>Design code</th>
                      <th>HSN</th>
                      <th className="num">Taxable</th>
                      <th className="num">GST</th>
                      <th className="num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {block.projects.map((project) => (
                      <React.Fragment key={project.row_number}>
                        <tr className={project.error ? 'is-error' : project.duplicate ? 'is-duplicate' : ''}>
                          <td>{project.row_number}</td>
                          <td>
                            {project.description || '—'}
                            {project.duplicate && !project.error && (
                              <span className="ipt-tag">Duplicate – will be skipped</span>
                            )}
                          </td>
                          <td>{project.design_code || '—'}</td>
                          <td>{project.hsn_code || '—'}</td>
                          <td className="num">{project.taxable_amount != null ? formatINR(project.taxable_amount) : '—'}</td>
                          <td className="num">{project.computed_gst != null ? formatINR(project.computed_gst) : '—'}</td>
                          <td className="num">{project.computed_total != null ? formatINR(project.computed_total) : '—'}</td>
                        </tr>
                        {project.error && (
                          <tr className="is-error ipt-error-row">
                            <td />
                            <td colSpan={6}>{project.error}</td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(blockErrors.length > 0 || block.warnings.length > 0) && (
              <ul className="ipt-messages">
                {blockErrors.map((msg) => (
                  <li key={`e-${msg}`} className="is-error"><FiAlertCircle /> {msg}</li>
                ))}
                {block.warnings.map((msg) => (
                  <li key={`w-${msg}`} className="is-warning"><FiAlertTriangle /> {msg}</li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default ImportPreviewTable;
